import type { UserData } from '@/auth/auth-api';
import { isPhlebotomistRole, isTechnicianRole } from '@/auth/roles';
import { isIntakeDeskOn } from '@/lib/intake-switches';
import { PERSISTABLE_NAV_IDS, type NavId } from './last-selected-nav';

/**
 * Role confinement for the left nav rail (PROD-2396).
 *
 * The problem this solves: a Technician (`tenantRoleId === '21'`) signs in and
 * gets the WHOLE application — Dashboard, Calendar, Studio, Patients, Reports,
 * Billing, Stock — with the one surface they actually operate, the Device
 * Sessions worklist, buried as a tab inside the Calendar. Andrew's
 * requirement (2026-09-20) is the inverse: a technician lands on the device
 * worklist and sees essentially nothing else.
 *
 * ── Flutter precedent ──────────────────────────────────────────────────────
 * Flutter already does exactly this, for a different role. In
 * `scrips_msp1_pm/lib/features/menu_container/data/datasources/
 * dummy_menu_data_source_impl.dart:22-28`, when `userData.isPhlebotomistRole`
 * the menu contents are filtered down to the single entry whose
 * `menuKey == 'sc_btn_menu_calendar'` and everything else is dropped —
 * before the ordinary permission filter even runs. `menu_widget.dart:123`
 * additionally drops the Profile row from the account popup for that role.
 * pm-react never ported any of it: `side-nav.tsx` has a hardcoded
 * `NAV_ITEMS` array with no role input at all.
 *
 * This module is that port, generalised. The mechanism is role-agnostic; the
 * ROSTER — `isConfinedRole` below, the one function to edit when a role joins
 * or leaves — is deliberately just Technician. Confinement additionally
 * requires the surface to exist: `isNavConfined` ANDs the roster with
 * `VITE_TECHNICIAN_WORKLIST_ENABLED`, so the flag kills the whole feature
 * and never leaves a role half-confined.
 *
 * The Phlebotomist (`'19'`) joined the roster on 23 Oct 2026 (Andrew: "a
 * phlebotomist should see ONLY the Order Requests screen, as before" - as
 * Flutter confines them). Confined to the calendar's Order Requests tab,
 * behind `VITE_LAB_LINKAGE_ENABLED`, the flag that already decides whether
 * that tab exists. The roster is the `CONFINED_ROLES` table below.
 *
 * ── WHAT THIS IS NOT ───────────────────────────────────────────────────────
 * This is navigation scoping and UX confinement. It is NOT a security
 * boundary and must never be described as one. The endpoints behind every
 * screen it hides enforce no role whatsoever (PROD-2388): `TenantRolePermissions`
 * is checked in QuestionBank and Inventory only, and a Technician account
 * carrying zero permissions was measured reading all 57 devices and getting a
 * 200 from `ResourceList`. A confined user who reaches for the API directly
 * gets the same data they always could. What this buys is (a) a coherent
 * product for someone whose whole job is one screen, and (b) a smaller client
 * cache, because a surface that never mounts never pulls rows into React
 * Query. It buys nothing at all against a determined authenticated caller.
 */

/**
 * The identity columns confinement reads. Same `Pick` shape the predicates in
 * `@/auth/roles` take, so a caller can hand over `auth-store`'s `user`
 * unchanged.
 */
export type NavConfinementUser = Pick<UserData, 'role' | 'tenantRoleId'>;

/**
 * The single nav a confined role keeps. Calendar, because the Device Sessions
 * worklist lives inside the scheduling frame as a tab — mirroring Flutter,
 * whose phlebotomist keeps `sc_btn_menu_calendar` for the same structural
 * reason. If the worklist is ever promoted to its own top-level nav, this
 * constant moves and nothing else does.
 */
export const CONFINED_NAV_ID: NavId = 'calendar';

/**
 * Path prefixes a confined user may still reach directly, on top of `/`
 * itself (see `confinedRedirectPath`).
 *
 * EMPTY, on purpose. Andrew's clarification (2026-09-20) is the strictest
 * reading — "one screen and nothing else" — so a confined role gets `/` and
 * nothing more. `/settings` was the one candidate: a Technician sits in the
 * STAFF bucket, and `ProfilePopover` shows Account Settings to that whole
 * bucket, so blocking the route would have turned a visible menu row into a
 * bounce. The answer was to remove the row, not to widen the allowlist —
 * which is also what Flutter does (`menu_widget.dart:123` drops the Profile
 * row from the account popup for its confined role). Logout stays; it clears
 * the session rather than navigating.
 *
 * Kept as a named constant rather than inlined so widening it later is a
 * visible, reviewable edit in one place.
 */
export const CONFINED_ALLOWED_PATH_PREFIXES: readonly string[] = [];

/** The calendar tab a confined role is kept on (the `SchedulingTab` ids of those two tabs). */
export type ConfinedTabId = 'technicianWorklist' | 'orderRequests';

interface ConfinedRole {
  /** Whether the user holds this role. */
  matches: (user: NavConfinementUser | null | undefined) => boolean;
  /** The one calendar tab the role keeps, and lands on. */
  tabId: ConfinedTabId;
  /** Whether that tab exists in this build: the flag that already gates it. */
  surfaceAvailable: () => boolean;
}

/**
 * The ROSTER: roles whose navigation collapses to one surface, each with the
 * calendar tab it keeps and the flag that decides whether that tab exists.
 * The only thing to edit when a role joins or leaves it. Checked in order.
 *
 *   Technician   ('21') -> Device Sessions worklist, VITE_TECHNICIAN_WORKLIST_ENABLED (PROD-2396)
 *   Phlebotomist ('19') -> Order Requests,           VITE_LAB_LINKAGE_ENABLED (23 Oct 2026, Flutter parity)
 */
const CONFINED_ROLES: readonly ConfinedRole[] = [
  {
    matches: isTechnicianRole,
    tabId: 'technicianWorklist',
    surfaceAvailable: () => import.meta.env.VITE_TECHNICIAN_WORKLIST_ENABLED === 'true',
  },
  {
    matches: isPhlebotomistRole,
    tabId: 'orderRequests',
    surfaceAvailable: () => import.meta.env.VITE_LAB_LINKAGE_ENABLED === 'true',
  },
];

/**
 * Whether the user holds a role on the roster.
 *
 * Role membership alone does NOT confine anyone: the surface has to exist
 * too. `isNavConfined` is the answer callers want.
 */
export function isConfinedRole(
  user: NavConfinementUser | null | undefined,
): boolean {
  return CONFINED_ROLES.some((r) => r.matches(user));
}

/**
 * DRIFT NOTE — the roster depends on exactly one export: `isTechnicianRole`
 * from `@/auth/roles`, which is `user.tenantRoleId === TECHNICIAN_TENANT_ROLE_ID`
 * (the `'21'` constant, verified — not an inline literal, and not the
 * label-matching `isTechnicianRole` in
 * `src/settings/services/use-technicians.ts`, which answers the same English
 * question from a different column and must never gate a surface).
 *
 * The consequence to see coming: if a second Technician variant is ever
 * folded into that predicate — a senior technician, a lead — the confinement
 * roster expands SILENTLY with it, and those users lose the whole
 * application at their next token refresh with no change to this file and no
 * test here failing. If you add a variant there, come back here and decide
 * whether it belongs on the roster; the answer is not automatically yes.
 */

/**
 * The calendar tab this session is confined to, or `null` when it is not
 * confined: the user's roster entry, when the surface that entry confines to
 * exists in this build. The Device Sessions worklist ships behind
 * `VITE_TECHNICIAN_WORKLIST_ENABLED` (PROD-2007); Order Requests behind
 * `VITE_LAB_LINKAGE_ENABLED`. This module reads those same flags rather than
 * taking a caller's word for it, so there is exactly one answer to "does the
 * surface exist".
 */
export function confinedTabOf(
  user: NavConfinementUser | null | undefined,
): ConfinedTabId | null {
  const entry = CONFINED_ROLES.find((r) => r.matches(user));
  return entry && entry.surfaceAvailable() ? entry.tabId : null;
}

/**
 * True when this session's navigation is actually confined: a role on the
 * roster AND a surface for it to be confined to. Every other export here
 * funnels through this, so the two halves can never be applied to different
 * parts of the app.
 *
 * WHY THE FLAG IS IN THE PREDICATE, not only at the calendar (Andrew
 * 2026-09-20). Confining the rail on role alone produced a state nobody
 * designed: with the flag off a Technician got a Calendar-only rail that
 * opened on the FULL practitioner calendar — every tab, the search, the
 * gear. Confined enough to be crippled, not confined enough to be coherent,
 * and it is exactly the state prod would be in the moment the role is issued
 * before the flag flips. The rule is that the confinement and the surface it
 * confines to move together:
 *
 *   flag ON  + Technician → confined to the Device Sessions worklist
 *   flag OFF + Technician → NOT confined; today's behaviour exactly, full
 *                           rail, full calendar
 *
 * Falling back to current behaviour is honest and reversible; the half-state
 * was neither. It also makes the flag a real kill-switch for the whole
 * feature instead of for half of it.
 *
 * This makes the module environment-dependent, which for a policy module is
 * worth saying out loud: a test must stub `VITE_TECHNICIAN_WORKLIST_ENABLED`
 * to observe confinement at all. `isConfinedRole` stays pure for the callers
 * that only care about the roster.
 */
export function isNavConfined(
  user: NavConfinementUser | null | undefined,
): boolean {
  return confinedTabOf(user) !== null;
}

/**
 * The nav ids this user may see, in rail order.
 *
 * Unconfined sessions — every role but Technician, a null/unknown user, AND
 * a Technician in a build where the worklist flag is off — get the full set
 * back, identical to today. The confined case returns exactly one id.
 *
 * Note the asymmetry, which is intentional: the function is fail-OPEN across
 * roles (unknown role ⇒ full nav, i.e. zero behaviour change for everyone who
 * is not on the roster) and fail-CLOSED within a confined role (a nav added
 * to `PERSISTABLE_NAV_IDS` tomorrow is automatically invisible to a
 * Technician, because the confined branch names its one allowed id rather
 * than subtracting the disallowed ones).
 */
export function visibleNavIds(
  user: NavConfinementUser | null | undefined,
): readonly NavId[] {
  return isNavConfined(user) ? [CONFINED_NAV_ID] : PERSISTABLE_NAV_IDS;
}

/**
 * Filter a rail's item list by role, preserving order.
 *
 * Generic over the item shape so the rail keeps owning its own icons and
 * labels — this module owns the POLICY, `side-nav.tsx` owns the pixels. That
 * split is what makes the policy unit-testable without rendering the shell.
 */
export function visibleNavItems<T extends { id: NavId }>(
  items: readonly T[],
  user: NavConfinementUser | null | undefined,
): T[] {
  const allowed = visibleNavIds(user);
  return items.filter((item) => allowed.includes(item.id));
}

/**
 * Whether a confined user may keep a restored `lastSelectedMenu` value.
 *
 * Session-resume restores the nav the user was last on
 * (`last-selected-nav.ts`). A Technician can only ever have persisted
 * `'calendar'` through the rail — but a stale key from before a role change
 * would otherwise drop them onto Patients with no way back but the one
 * remaining rail button. Cheap to hold.
 */
export function allowsNavId(
  user: NavConfinementUser | null | undefined,
  id: NavId,
): boolean {
  return visibleNavIds(user).includes(id);
}

/**
 * The slice of `react-router`'s location the guard needs. An object rather
 * than a bare pathname so a caller cannot forget that the QUERY STRING is
 * part of the decision — `/?actor=devices` is a different surface from `/`
 * (PROD-2398), and the signature should make that impossible to overlook.
 */
export interface ConfinedLocation {
  pathname: string;
  search: string;
}

/**
 * Query params a confined session must not carry on `/`.
 *
 * READ THIS BEFORE ADDING ONE. The first version reused
 * `ROOT_DEEP_LINK_PARAMS` (`['date', 'view', 'actor']`) on the reasoning
 * that a param added there later would be stripped automatically. That
 * shipped a REDIRECT LOOP - ~37 navigations/sec, 4,172 aborted requests in
 * a 90s session - because `date` and `view` are not user intent at all:
 * `scheduling-page.tsx` writes them back into the URL on every render of
 * the calendar (`setSearchParams({date, view}, {replace: true})`). Strip,
 * the page rewrites, strip. Forever.
 *
 * The existing strip effect in `App.tsx` survives that same hazard only
 * because it bails on `activeNav === 'calendar'` - and a confined
 * Technician is ALWAYS on 'calendar', it being their only nav. So the one
 * user this feature exists for is the one user that protection cannot
 * cover, and this list has to be right on its own.
 *
 * The rule: a param belongs here ONLY if nothing rewrites it unprompted.
 * `actor` qualifies - it is written from state the page seeds FROM the URL,
 * so once the URL stops saying it the page stops saying it too
 * (`writeCalendarActor` DELETES the param for the default actor). `date`
 * and `view` do not qualify and never will.
 */
export const CONFINED_STRIPPED_PARAMS: readonly string[] = ['actor'];

/** True when `search` carries a param a confined session must not hold. */
function hasStrippedParam(search: string): boolean {
  try {
    const params = new URLSearchParams(search);
    return CONFINED_STRIPPED_PARAMS.some((p) => params.get(p) !== null);
  } catch {
    // An unparseable query string is not something to wave through.
    return true;
  }
}

/**
 * Route-level companion to the rail filter: where a confined user should be
 * sent when the URL points at a surface their rail no longer offers, or
 * `null` when the URL is fine as-is.
 *
 * WHY A ROUTE GUARD AT ALL, when Flutter only filters the menu: filtering the
 * rail removes the LINKS, not the ROUTES. `/patients`, `/billing`, `/stock`,
 * `/reports`, `/studio` and `/scheduling/appointment/:id` all still render if
 * typed, bookmarked, restored by the browser on reopen, or pasted by a
 * colleague — and a bookmark surviving a role change is the likely real-world
 * case, not a malicious one. A rail that says "you have one screen" next to an
 * address bar that says otherwise is a half-built feature. Flutter's routes
 * are menu-driven so the question does not arise there; React's are not.
 *
 * It fails CLOSED on PATHS: the allowlist is `/` plus
 * `CONFINED_ALLOWED_PATH_PREFIXES`, so any route added to the app later is
 * out of reach for a confined user by default rather than by remembering to
 * add it here.
 *
 * It deliberately does NOT fail closed on QUERY PARAMS. That asymmetry is
 * the scar from a redirect loop: an unknown param is harmless, while
 * stripping one the page rewrites unprompted is fatal. Params are named OUT
 * one at a time, on evidence, in `CONFINED_STRIPPED_PARAMS`.
 *
 * It CANNOT affect any other role: the first line returns `null` for everyone
 * who is not confined, before the pathname is even looked at.
 *
 * The destination is `/` (the calendar, which for a confined technician opens
 * on the Device Sessions worklist) rather than a blank frame or a 403 page. A
 * technician who clicked a stale bookmark gets put to work; a dead-end screen
 * would just be a support ticket. There is no message: the honest one would
 * be "you cannot see this", and that is not true — the data is still reachable
 * through the API (PROD-2388). Silently landing them on their own surface
 * claims less.
 */
export function confinedRedirectPath(
  location: ConfinedLocation,
  user: NavConfinementUser | null | undefined,
): string | null {
  if (!isNavConfined(user)) return null;

  if (location.pathname === '/') {
    // `/` is the confined surface, but its QUERY STRING is not automatically
    // innocent - `?actor=devices` is a different surface (PROD-2398).
    //
    // WHAT THE STRIP ACTUALLY CLOSES, since an earlier version of this
    // comment called it hygiene and that was wrong: `scheduling-page.tsx`
    // derives `isDevicesTab` from the `actor` PARAM, not from the active
    // tab, and feeds it straight to `useCalendarDevices(practiceId,
    // isDevicesTab)`, `useDeviceCalendarSlots(params, isDevicesTab)` and
    // `enabled: isDevicesTab && ...` on the day-appointments query. So a
    // confined Technician sitting on `/?actor=devices` pulls device
    // calendar slots and device appointments into the React Query cache
    // even though the worklist is the only thing on screen. The Book Device
    // button gate cannot reach that; only clearing the param can - same
    // "a surface that never mounts never pulls rows" reasoning as the
    // notification panel.
    //
    // ONLY `CONFINED_STRIPPED_PARAMS` - read that constant's comment before
    // widening it. Stripping `date`/`view` here caused a redirect loop.
    return hasStrippedParam(location.search) ? '/' : null;
  }

  if (
    CONFINED_ALLOWED_PATH_PREFIXES.some(
      (prefix) =>
        location.pathname === prefix ||
        location.pathname.startsWith(`${prefix}/`),
    )
  ) {
    return null;
  }
  // The lab desk's review (Andrew, 23 Oct, demo flow A: the uploader reviews and confirms): a Phlebotomist on Order
  // Requests opens "Review values" at the review page and comes back. Only that one page, only while the desk is on.
  if (confinedTabOf(user) === 'orderRequests' && isIntakeDeskOn() && DESK_REVIEW_PATH.test(location.pathname)) {
    return null;
  }
  return '/';
}

/** The intake review page (`patients-routes.tsx`, `intakeReviewPath`): `/patients/edit/:patientId/intake/:runId`. */
const DESK_REVIEW_PATH = /^\/patients\/edit\/[^/]+\/intake\/[^/]+\/?$/;
